type RouteClientPreloadKey = "student-list";

type RouteClientPreloadEntry = {
  key: RouteClientPreloadKey;
  preload: () => Promise<unknown>;
};

const routeClientPreloadPromises = new Map<RouteClientPreloadKey, Promise<void>>();

function getPathname(href: string): string | null {
  const currentOrigin = typeof window === "undefined"
    ? "https://app.iebridge.com"
    : window.location.origin;

  try {
    const url = new URL(href, currentOrigin);
    return url.origin === currentOrigin ? url.pathname : null;
  } catch {
    return null;
  }
}

export function getRouteClientPreloadKey(href: string): RouteClientPreloadKey | null {
  const pathname = getPathname(href);
  if (!pathname) {
    return null;
  }

  const segments = pathname.split("/").filter(Boolean);
  const isOrganizationStudentList = segments.length === 2
    && segments[1] === "students";
  const isAgencyStudentList = segments.length === 3
    && segments[0] === "a"
    && segments[2] === "students";

  if (isOrganizationStudentList || isAgencyStudentList) {
    return "student-list";
  }

  return null;
}

function getRouteClientPreloadEntry(href: string): RouteClientPreloadEntry | null {
  switch (getRouteClientPreloadKey(href)) {
    case "student-list": {
      return {
        key: "student-list",
        // Agency is a default-visible Placement column for organization and
        // agency Students lists. Reusing its exact lazy import warms that
        // first-render module without compiling a duplicate route root.
        preload: () => import("@/components/display/AgencyDisplay"),
      };
    }
    default: {
      return null;
    }
  }
}

function reportRouteClientPreloadError(error: unknown) {
  if (typeof globalThis.reportError === "function") {
    globalThis.reportError(error);
  }
}

/**
 * Warms client modules that are expensive on their first render. Entries stay
 * deliberately narrow and intent-only: the route cache remains owned by Next,
 * while this catalog removes first-click initialization for exact lazy modules
 * already owned by a measured list configuration.
 */
export function preloadRouteClientForHref(href: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const entry = getRouteClientPreloadEntry(href);
  if (!entry || routeClientPreloadPromises.has(entry.key)) {
    return;
  }

  const preloadPromise = entry.preload()
    .then(() => undefined)
    .catch((error: unknown) => {
      routeClientPreloadPromises.delete(entry.key);
      reportRouteClientPreloadError(error);
    });
  routeClientPreloadPromises.set(entry.key, preloadPromise);
}
